import { useAuth0 } from "@auth0/auth0-react";
import axios, { AxiosRequestConfig } from "axios";
import { useCallback, useMemo } from "react";

import { CatalogImageLookup, CatalogImageResult } from "./images";
import {
  CatalogSearchResult,
  ChainExecutionResponse,
  FoodLabelLink,
  GroundingPayload,
  IngestReviewItem,
  IngestReviewResult,
  LabelAutoMatchInput,
  LabelAutoMatchResult,
  LabelSearchResult,
  MergeResult,
  PassioFood,
  RecheckResult,
  ReplacementItem,
  ReplacementResult,
  ReplacementReviewEntry,
  ReplacementUnmatchedEntry,
  WebAlternative,
} from "./types";

const audience = process.env.REACT_APP_AUTH0_AUDIENCE || "";

// ===== Chain id configuration (env default + localStorage runtime override) =====

const LS_INGEST_KEY = "catalogManager.ingestChainId";
const LS_MERGE_KEY = "catalogManager.mergeChainId";
const LS_REPLACE_KEY = "catalogManager.replacementChainId";
const LS_ALT_FINDER_KEY = "catalogManager.altFinderChainId";
const LS_AUTO_MERGE_KEY = "catalogManager.autoMergeDuplicates";

// Built-in fallback chain ids. These are used ONLY when the corresponding
// REACT_APP_CATALOG_*_CHAIN_ID env var (injected from Duplo secrets in deployed
// envs) is not set. Precedence for the value actually used at runtime is:
//   per-browser localStorage override  >  env var (Duplo secret)  >  these.
// Update these when the canonical chain ids change so a fresh browser with no
// env var still points at working chains.
const DEFAULT_INGEST_CHAIN_ID = "382ceea1-7651-4c7f-accd-3f70045942b5";
const DEFAULT_MERGE_CHAIN_ID = "c6c2c0a6-61d2-40b4-a495-eb1fd90e745c";
const DEFAULT_REPLACEMENT_CHAIN_ID = "f594d877-9376-4426-b9a5-c3ed6474863a";
const DEFAULT_ALT_FINDER_CHAIN_ID = "22639114-c82e-4079-bb28-5941b61857d4";

// Whether an actual env var was provided (vs. falling back to the built-in
// default) — surfaced in the Settings UI so it's clear if the Duplo secret is
// being picked up.
const envIngestFromEnv = !!process.env.REACT_APP_CATALOG_INGEST_CHAIN_ID;
const envMergeFromEnv = !!process.env.REACT_APP_CATALOG_MERGE_CHAIN_ID;
const envReplacementFromEnv =
  !!process.env.REACT_APP_CATALOG_REPLACEMENT_CHAIN_ID;
const envAltFinderFromEnv = !!process.env.REACT_APP_CATALOG_ALT_FINDER_CHAIN_ID;

// The baseline (non-localStorage) value: env var if present, else built-in
// default. This is what the "Reset to env defaults" button restores to.
const envIngestChainId =
  process.env.REACT_APP_CATALOG_INGEST_CHAIN_ID || DEFAULT_INGEST_CHAIN_ID;
const envMergeChainId =
  process.env.REACT_APP_CATALOG_MERGE_CHAIN_ID || DEFAULT_MERGE_CHAIN_ID;
const envReplacementChainId =
  process.env.REACT_APP_CATALOG_REPLACEMENT_CHAIN_ID ||
  DEFAULT_REPLACEMENT_CHAIN_ID;
const envAltFinderChainId =
  process.env.REACT_APP_CATALOG_ALT_FINDER_CHAIN_ID ||
  DEFAULT_ALT_FINDER_CHAIN_ID;

const readLs = (key: string): string => {
  try {
    return (window.localStorage.getItem(key) || "").trim();
  } catch {
    return "";
  }
};

export const getIngestChainId = (): string =>
  readLs(LS_INGEST_KEY) || envIngestChainId;
export const getMergeChainId = (): string =>
  readLs(LS_MERGE_KEY) || envMergeChainId;
export const getReplacementChainId = (): string =>
  readLs(LS_REPLACE_KEY) || envReplacementChainId;
export const getAltFinderChainId = (): string =>
  readLs(LS_ALT_FINDER_KEY) || envAltFinderChainId;

export const setIngestChainIdOverride = (value: string): void => {
  try {
    if (value.trim()) window.localStorage.setItem(LS_INGEST_KEY, value.trim());
    else window.localStorage.removeItem(LS_INGEST_KEY);
  } catch {
    /* ignore */
  }
};
export const setMergeChainIdOverride = (value: string): void => {
  try {
    if (value.trim()) window.localStorage.setItem(LS_MERGE_KEY, value.trim());
    else window.localStorage.removeItem(LS_MERGE_KEY);
  } catch {
    /* ignore */
  }
};
export const setReplacementChainIdOverride = (value: string): void => {
  try {
    if (value.trim()) window.localStorage.setItem(LS_REPLACE_KEY, value.trim());
    else window.localStorage.removeItem(LS_REPLACE_KEY);
  } catch {
    /* ignore */
  }
};
export const setAltFinderChainIdOverride = (value: string): void => {
  try {
    if (value.trim())
      window.localStorage.setItem(LS_ALT_FINDER_KEY, value.trim());
    else window.localStorage.removeItem(LS_ALT_FINDER_KEY);
  } catch {
    /* ignore */
  }
};

// Clear all per-browser chain-id overrides so each falls back to its env var
// (Duplo secret) or built-in default. Backs the "Reset to env defaults" button
// so you can wipe local tweaks and return to the deployed configuration.
export const resetChainIdsToEnv = (): void => {
  try {
    [LS_INGEST_KEY, LS_MERGE_KEY, LS_REPLACE_KEY, LS_ALT_FINDER_KEY].forEach(
      (k) => window.localStorage.removeItem(k)
    );
  } catch {
    /* ignore */
  }
};

// Auto-run the AI compare/merge chain for every duplicate as soon as the review
// page loads. Defaults to ON (the user almost always wants the diff ready).
export const getAutoMergeDuplicates = (): boolean => {
  const v = readLs(LS_AUTO_MERGE_KEY);
  return v === "" ? true : v === "true";
};
export const setAutoMergeDuplicates = (value: boolean): void => {
  try {
    window.localStorage.setItem(LS_AUTO_MERGE_KEY, value ? "true" : "false");
  } catch {
    /* ignore */
  }
};

export const chainIdConfig = () => ({
  ingest: {
    value: getIngestChainId(),
    envDefault: envIngestChainId,
    fromEnv: envIngestFromEnv,
  },
  merge: {
    value: getMergeChainId(),
    envDefault: envMergeChainId,
    fromEnv: envMergeFromEnv,
  },
  replacement: {
    value: getReplacementChainId(),
    envDefault: envReplacementChainId,
    fromEnv: envReplacementFromEnv,
  },
  altFinder: {
    value: getAltFinderChainId(),
    envDefault: envAltFinderChainId,
    fromEnv: envAltFinderFromEnv,
  },
  autoMerge: getAutoMergeDuplicates(),
});

// ===== Error helpers =====

export const extractApiError = (
  err: unknown,
  fallback = "Request failed"
): string => {
  const anyErr = err as {
    response?: { data?: unknown; status?: number };
    message?: string;
  };
  const data = anyErr?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    const msg = obj.message || obj.error || obj.detail;
    if (typeof msg === "string" && msg.trim()) return msg.trim();
  }
  if (anyErr?.message) return anyErr.message;
  return fallback;
};

/**
 * Products read routes return one of two shapes:
 *  - a bare `passio.Food` (e.g. `GET /products/food/{id}`)
 *  - a `passio.FoodResults` envelope `{ results: [{ ingredients: [Food, ...] }] }`
 *    (e.g. `GET /products/food/search/result/refCode/{refCode}` and the
 *    productCode lookup), where the food itself is the first ingredient of the
 *    first result.
 * This normalizes either into a single editable `PassioFood` (or null).
 */
export const extractFoodFromDetail = (data: unknown): PassioFood | null => {
  if (!data || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;

  // Bare food: has an id (and isn't an envelope).
  if (typeof obj.id === "string" && !("results" in obj)) {
    return obj as PassioFood;
  }

  const results = obj.results;
  if (Array.isArray(results) && results.length > 0) {
    const first = results[0] as Record<string, unknown>;
    const ingredients = first?.ingredients;
    if (Array.isArray(ingredients) && ingredients.length > 0) {
      const food = ingredients[0] as Record<string, unknown>;
      if (food && typeof food.id === "string") return food as PassioFood;
    }
    if (first && typeof first.id === "string") return first as PassioFood;
  }
  return null;
};

export const isDuplicateUpcError = (err: unknown): boolean => {
  const anyErr = err as { response?: { status?: number } };
  const status = anyErr?.response?.status;
  const msg = extractApiError(err, "").toLowerCase();
  return status === 412 || msg.includes("duplicate upc");
};

// ===== Chain output extraction (node_results are keyed by node id) =====

// Each node result is a wrapper: { status, input, output, startTime, metadata }.
// `valuesOf` returns those wrappers (used for reading sibling `.metadata`);
// `outputsOf` unwraps each to its `.output` (the actual node output payload),
// falling back to the wrapper itself for any non-wrapped shape.
const valuesOf = (
  nodeResults: Record<string, unknown> | null | undefined
): unknown[] => {
  if (!nodeResults || typeof nodeResults !== "object") return [];
  return Object.values(nodeResults);
};

const outputsOf = (
  nodeResults: Record<string, unknown> | null | undefined
): unknown[] =>
  valuesOf(nodeResults).map((v) =>
    v && typeof v === "object" && "output" in (v as Record<string, unknown>)
      ? (v as { output: unknown }).output
      : v
  );

/**
 * Score how strongly an item resembles a *final* assemble-node review item.
 * The search/match node also emits `{ items: [...] }` carrying `existing_matches`
 * (but no `proposed`/`decision`/`ingest_payload`), so a plain "has any of these
 * keys" check would mistakenly select that earlier node and render an empty
 * proposed record. Scoring lets the assemble node (which has all of them) win
 * while still surfacing a search-only node if that's all we have.
 */
const ingestItemScore = (x: unknown): number => {
  if (!x || typeof x !== "object") return 0;
  const o = x as Record<string, unknown>;
  let s = 0;
  if ("ingest_payload" in o) s += 4;
  if ("decision" in o) s += 3;
  if ("proposed" in o) s += 2;
  if ("existing_matches" in o || "exact_upc_match" in o) s += 1;
  return s;
};

/**
 * Collect display-only web-search grounding from any node, keyed by item index.
 * The research step is a mapOverArray node, so it stores
 * `metadata.grounding_items: [{ index, ... }]`. A non-mapped node stores a
 * single `metadata.grounding` (treated as index 0). Returns a sparse map.
 */
export const extractGroundingByIndex = (
  resp: ChainExecutionResponse
): Record<number, GroundingPayload> => {
  const out: Record<number, GroundingPayload> = {};
  for (const v of valuesOf(resp.node_results)) {
    if (!v || typeof v !== "object") continue;
    const meta = (v as { metadata?: unknown }).metadata;
    if (!meta || typeof meta !== "object") continue;
    const items = (meta as { grounding_items?: unknown }).grounding_items;
    if (Array.isArray(items)) {
      items.forEach((g, i) => {
        if (g && typeof g === "object") {
          const gp = g as GroundingPayload;
          const idx = typeof gp.index === "number" ? gp.index : i;
          out[idx] = gp;
        }
      });
    }
    const single = (meta as { grounding?: unknown }).grounding;
    if (single && typeof single === "object" && out[0] === undefined) {
      out[0] = single as GroundingPayload;
    }
  }
  return out;
};

export const extractIngestResult = (
  resp: ChainExecutionResponse
): IngestReviewResult => {
  const grounding = extractGroundingByIndex(resp);
  const attach = (items: IngestReviewItem[]): IngestReviewItem[] =>
    items.map((it, i) =>
      grounding[i] && !it.__grounding
        ? { ...it, __grounding: grounding[i] }
        : it
    );

  const outputs = outputsOf(resp.node_results);

  // 1) Pick the `{ items: [...] }` node whose items most strongly resemble final
  //    ingest-review items. Multiple nodes emit `{ items: [...] }` (organize,
  //    search/match, assemble); only the assemble node carries the full
  //    `proposed`/`decision`/`ingest_payload`, so we score and take the best
  //    rather than the first match.
  let best: {
    items: unknown[];
    summary?: Record<string, unknown>;
    score: number;
  } | null = null;
  for (const v of outputs) {
    if (!v || typeof v !== "object") continue;
    const items = (v as { items?: unknown }).items;
    if (!Array.isArray(items) || items.length === 0) continue;
    const score = items.reduce(
      (m: number, it) => Math.max(m, ingestItemScore(it)),
      0
    );
    if (score <= 0) continue;
    if (!best || score > best.score) {
      best = {
        items,
        summary: (v as { summary?: Record<string, unknown> }).summary,
        score,
      };
    }
  }
  if (best) {
    return {
      items: attach(best.items as IngestReviewItem[]),
      summary: best.summary,
    };
  }

  // 2) A bare array of ingest-like items.
  for (const v of outputs) {
    if (Array.isArray(v) && v.some((it) => ingestItemScore(it) > 0)) {
      return { items: attach(v as IngestReviewItem[]) };
    }
  }
  // 3) Last resort: a node carrying a `summary` (the assemble node) even if
  //    it produced zero items, so we surface its (empty) result accurately.
  for (const v of outputs) {
    if (
      v &&
      typeof v === "object" &&
      Array.isArray((v as { items?: unknown }).items) &&
      "summary" in (v as Record<string, unknown>)
    ) {
      return {
        items: attach((v as { items: unknown[] }).items as IngestReviewItem[]),
        summary: (v as { summary?: Record<string, unknown> }).summary,
      };
    }
  }
  return { items: [] };
};

/**
 * Extract the grocery-replacement chain's final output. The assemble node emits
 * `{ items: [{ original_item, suggested_replacements, ... }], summary }`; score
 * candidate nodes so the final assemble node (which carries
 * `suggested_replacements`/`original_item_details`) wins over earlier
 * `{ items: [...] }` nodes (parse/search).
 */
export const extractReplacementResult = (
  resp: ChainExecutionResponse
): ReplacementResult => {
  const replacementScore = (x: unknown): number => {
    if (!x || typeof x !== "object") return 0;
    const o = x as Record<string, unknown>;
    let s = 0;
    if ("suggested_replacements" in o) s += 3;
    if ("original_item_details" in o) s += 2;
    if ("original_item" in o) s += 1;
    return s;
  };
  let best: {
    items: unknown[];
    summary?: Record<string, unknown>;
    score: number;
  } | null = null;
  for (const v of outputsOf(resp.node_results)) {
    if (!v || typeof v !== "object") continue;
    const items = (v as { items?: unknown }).items;
    if (!Array.isArray(items) || items.length === 0) continue;
    const score = items.reduce(
      (m: number, it) => Math.max(m, replacementScore(it)),
      0
    );
    if (score <= 0) continue;
    if (!best || score > best.score) {
      best = {
        items,
        summary: (v as { summary?: Record<string, unknown> }).summary,
        score,
      };
    }
  }
  if (best) {
    return {
      items: best.items as ReplacementItem[],
      summary: best.summary,
    };
  }
  return { items: [] };
};

/**
 * Extract the Web Product Finder chain's combined output. The `combine_web_alternatives`
 * plugin emits `{ products: [{name, brand, for_item}], count }`. As a fallback (if the
 * chain has no combine step) we flatten any node that is the mapOverArray output —
 * an array of `{ original, alternatives: [{name, brand}] }`.
 */
export const extractWebAlternatives = (
  resp: ChainExecutionResponse
): WebAlternative[] => {
  const clean = (arr: unknown): WebAlternative[] => {
    if (!Array.isArray(arr)) return [];
    const out: WebAlternative[] = [];
    const seen = new Set<string>();
    for (const it of arr) {
      if (!it || typeof it !== "object") continue;
      const o = it as Record<string, unknown>;
      const name = typeof o.name === "string" ? o.name.trim() : "";
      if (!name) continue;
      const brand =
        typeof o.brand === "string" && o.brand.trim() ? o.brand.trim() : null;
      const key = `${name.toLowerCase()}|${(brand || "").toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        name,
        brand,
        for_item: typeof o.for_item === "string" ? o.for_item : null,
      });
    }
    return out;
  };

  // Preferred: the combine plugin's `{ products: [...] }`.
  for (const v of outputsOf(resp.node_results)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const products = (v as { products?: unknown }).products;
      if (Array.isArray(products) && products.length > 0) {
        const cleaned = clean(products);
        if (cleaned.length) return cleaned;
      }
    }
  }

  // Fallback: flatten a mapOverArray node ([{original, alternatives:[...]}]).
  for (const v of outputsOf(resp.node_results)) {
    if (!Array.isArray(v)) continue;
    const flat: WebAlternative[] = [];
    for (const entry of v) {
      if (!entry || typeof entry !== "object") continue;
      const alts = (entry as { alternatives?: unknown }).alternatives;
      const forItem = (entry as { original?: unknown }).original;
      if (!Array.isArray(alts)) continue;
      for (const a of alts) {
        if (a && typeof a === "object") {
          flat.push({
            ...(a as Record<string, unknown>),
            for_item: typeof forItem === "string" ? forItem : null,
          } as WebAlternative);
        }
      }
    }
    const cleaned = clean(flat);
    if (cleaned.length) return cleaned;
  }

  return [];
};

const looksLikeMergeResult = (x: unknown): boolean => {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return "merged" in o || "recommendation" in o;
};

export const extractMergeResults = (
  resp: ChainExecutionResponse
): MergeResult[] => {
  for (const v of outputsOf(resp.node_results)) {
    if (Array.isArray(v) && v.some(looksLikeMergeResult)) {
      return v as MergeResult[];
    }
    if (v && typeof v === "object") {
      const results = (v as { results?: unknown }).results;
      if (Array.isArray(results) && results.some(looksLikeMergeResult)) {
        return results as MergeResult[];
      }
      if (looksLikeMergeResult(v)) return [v as MergeResult];
    }
  }
  return [];
};

// ===== Hook =====

/** Per-node progress snapshot for the run overlay. */
export interface ChainNodeStatus {
  id: string;
  status: string; // pending | running | completed | failed
  progress?: string; // e.g. "4/6 items"
  error?: string;
}

export interface ChainProgress {
  status: string;
  credits?: number | null;
  nodes: ChainNodeStatus[];
  currentNodeId?: string;
}

/** Extract sorted per-node progress from a chain execution response. */
export const extractChainProgress = (
  resp: ChainExecutionResponse
): ChainProgress => {
  const nr = resp.node_results;
  const nodes: ChainNodeStatus[] = [];
  if (nr && typeof nr === "object") {
    for (const [id, raw] of Object.entries(nr)) {
      const v = (raw || {}) as Record<string, unknown>;
      nodes.push({
        id,
        status: typeof v.status === "string" ? v.status : "pending",
        progress: typeof v.progress === "string" ? v.progress : undefined,
        error: typeof v.error === "string" ? v.error : undefined,
      });
    }
  }
  // Sort by the numeric suffix of node ids (node-1, node-2, …) when present.
  const idNum = (id: string): number => {
    const m = id.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  };
  nodes.sort((a, b) => idNum(a.id) - idNum(b.id));
  const current = nodes.find((n) => n.status === "running");
  return {
    status: resp.status,
    credits: resp.total_ai_credits_used,
    nodes,
    currentNodeId: current?.id,
  };
};

/** Which slice of a customer's catalog history to load. */
export type ContributionsFilter = "all" | "added" | "edited";

/** A page of the current customer's catalog contributions. */
export interface ContributionsResult {
  items: PassioFood[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RunChainOptions {
  nodeOverrides?: Record<string, Record<string, unknown>>;
  metadata?: Record<string, unknown>;
  onStatus?: (status: string, credits?: number | null) => void;
  onProgress?: (progress: ChainProgress) => void;
  pollIntervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
}

export const useCatalogApi = () => {
  const { getAccessTokenSilently, user } = useAuth0();

  const authHeader = useCallback(async (): Promise<string> => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience,
        scope: "openid profile email offline_access",
      },
    });
    return `Bearer ${token}`;
  }, [getAccessTokenSilently]);

  const request = useCallback(
    async <T>(config: AxiosRequestConfig): Promise<T> => {
      const Authorization = await authHeader();
      const res = await axios({
        ...config,
        headers: { ...(config.headers || {}), Authorization },
      });
      return res.data as T;
    },
    [authHeader]
  );

  // ----- food reads (products API) -----
  // All catalog reads go through the products API. Search uses the advanced
  // search route, which returns lightweight summaries carrying a `refCode`; the
  // full editable record is then fetched on demand via that refCode.

  const searchFoods = useCallback(
    async (term: string, limit = 150): Promise<CatalogSearchResult[]> => {
      const data = await request<{ results?: Array<Record<string, unknown>> }>({
        method: "get",
        url: "/products/food/search/advanced",
        params: { term, limit },
      });
      const results = data?.results || [];
      return results
        .map((r) => ({
          refCode: String(r.refCode || ""),
          displayName: String(r.displayName || ""),
          brandName: (r.brandName as string) || undefined,
          resultId: (r.resultId as string) || undefined,
          productCode: (r.productCode as string) || undefined,
          type: (r.type as string) || undefined,
          iconId: (r.iconId as string) || undefined,
          score: typeof r.score === "number" ? (r.score as number) : undefined,
          tags: Array.isArray(r.tags) ? (r.tags as string[]) : undefined,
          concerns: Array.isArray(r.concerns)
            ? (r.concerns as number[])
            : undefined,
        }))
        .filter((r) => r.refCode && r.displayName);
    },
    [request]
  );

  // Fetch the full editable food for a search result via its refCode.
  const getFoodByRefCode = useCallback(
    async (refCode: string): Promise<PassioFood | null> => {
      const data = await request<unknown>({
        method: "get",
        url: `/products/food/search/result/refCode/${encodeURIComponent(
          refCode
        )}`,
      });
      return extractFoodFromDetail(data);
    },
    [request]
  );

  const getFoodById = useCallback(
    async (id: string): Promise<PassioFood> => {
      const data = await request<unknown>({
        method: "get",
        url: `/products/food/${encodeURIComponent(id)}`,
      });
      const food = extractFoodFromDetail(data);
      if (!food) throw new Error(`Food not found for id ${id}`);
      return food;
    },
    [request]
  );

  const getFoodByUpc = useCallback(
    async (upc: string): Promise<PassioFood | null> => {
      try {
        const data = await request<unknown>({
          method: "get",
          url: `/products/food/productCode/${encodeURIComponent(upc)}`,
        });
        return extractFoodFromDetail(data);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    [request]
  );

  // ----- food writes (products ingest passthrough, PRO-gated) -----
  // Writes go through the products API's PRO-gated catalog ingest passthrough,
  // which forwards to search_api server-side. Keeps every catalog call on the
  // products API and enforces the PRO subscription tier on the route.

  // `executionId`, when provided, is the EasyAI chain execution that produced
  // this record; the server stamps it as the origin id (source stays
  // catalog:add/edit:<hash>) so a write can be traced back to its run history.
  const createFood = useCallback(
    (food: PassioFood, executionId?: string): Promise<PassioFood> => {
      const { id, ...newFood } = food; // create assigns its own id
      void id;
      return request<PassioFood>({
        method: "post",
        url: "/products/sdk/catalog/food",
        data: newFood,
        headers: executionId
          ? { "X-Catalog-Execution-Id": executionId }
          : undefined,
      });
    },
    [request]
  );

  const updateFood = useCallback(
    (food: PassioFood, executionId?: string): Promise<PassioFood> => {
      if (!food.id) throw new Error("updateFood requires food.id");
      return request<PassioFood>({
        method: "put",
        url: `/products/sdk/catalog/food/${encodeURIComponent(food.id)}`,
        data: food,
        headers: executionId
          ? { "X-Catalog-Execution-Id": executionId }
          : undefined,
      });
    },
    [request]
  );

  // ----- re-check concern codes (re-run the hardcoded sanity checks) -----
  // Sends an (edited) internal food to the products passthrough, which proxies
  // to the b2b `recheck_food_concerns` plugin: it re-applies concern flags and
  // returns the refreshed record + codes/details + an ingest decision. No write.

  const recheckConcerns = useCallback(
    async (food: PassioFood): Promise<RecheckResult> => {
      const data = await request<{
        food?: PassioFood;
        passio_food?: PassioFood;
        concern_codes?: number[];
        concern_details?: RecheckResult["concern_details"];
        serving_view?: Record<string, unknown> | null;
        decision?: RecheckResult["decision"];
        blocking?: boolean;
      }>({
        method: "post",
        url: "/products/sdk/catalog/food/recheck",
        data: { food },
      });
      const out = data?.food || data?.passio_food || food;
      return {
        food: out,
        concern_codes: Array.isArray(data?.concern_codes)
          ? data.concern_codes
          : Array.isArray(out.concerns)
          ? (out.concerns as number[])
          : [],
        concern_details: data?.concern_details || [],
        serving_view: data?.serving_view ?? null,
        decision: data?.decision,
        blocking: data?.blocking,
      };
    },
    [request]
  );

  // ----- grocery replacement review feedback -----
  // Saves a reviewer's good/bad ratings of replacement suggestions. Proxies to
  // the b2b `save_grocery_replacement_review` plugin, which writes ONE JSON file
  // to the customer's bucket (date + chain run id in the name). No DB write.
  const saveReplacementReview = useCallback(
    async (args: {
      chainRunId: string;
      reviews: ReplacementReviewEntry[];
      unmatched?: ReplacementUnmatchedEntry[];
      groceryInput?: string;
    }): Promise<{
      saved: boolean;
      gcs_path?: string;
      review_count?: number;
      unmatched_count?: number;
    }> =>
      request({
        method: "post",
        url: "/products/sdk/catalog/replacement-review",
        data: {
          chain_run_id: args.chainRunId,
          reviews: args.reviews,
          unmatched_items: args.unmatched,
          grocery_input: args.groceryInput,
        },
      }),
    [request]
  );

  // ----- my contributions (records this customer added/edited) -----
  // Reads the current customer's own catalog history via the products API. The
  // server matches records by a hash of the auth-token customer id embedded in
  // the origin source, so no id is passed from the client. Paginated.

  const getMyContributions = useCallback(
    async (
      filter: ContributionsFilter = "all",
      page = 1,
      pageSize = 25
    ): Promise<ContributionsResult> => {
      const data = await request<{
        entities?: PassioFood[];
        total?: number;
        page?: number;
        pageSize?: number;
      }>({
        method: "get",
        url: "/products/sdk/catalog/food/contributions",
        params: { filter, page, pageSize },
      });
      return {
        items: Array.isArray(data?.entities) ? data.entities : [],
        total: typeof data?.total === "number" ? data.total : 0,
        page: typeof data?.page === "number" ? data.page : page,
        pageSize: typeof data?.pageSize === "number" ? data.pageSize : pageSize,
      };
    },
    [request]
  );

  // ----- ops change notification (best-effort) -----

  const notifyChange = useCallback(
    async (params: {
      action: "create" | "update";
      food: PassioFood;
      original?: PassioFood | null;
      customerName?: string;
      /** displayName of the label this food was linked to (empty if unlinked). */
      linkedLabel?: string;
      /** category phrases proposed for this item (shown when unlinked). */
      candidateLabels?: string[];
    }): Promise<void> => {
      try {
        await request({
          method: "post",
          url: "/products/sdk/catalog/notify-change",
          data: {
            action: params.action,
            food: params.food,
            original: params.original ?? undefined,
            actor_email: user?.email || "",
            customer_name: params.customerName || "",
            linked_label: params.linkedLabel || "",
            candidate_labels: params.candidateLabels || undefined,
          },
        });
      } catch {
        // Notification is best-effort; never block the write on email failure.
      }
    },
    [request, user]
  );

  // ----- label linking (link a food to a canonical label so it surfaces) -----
  // These proxy assets_api / nutrition_api through the products /sdk passthrough
  // (PRO-gated). A food only gets a first-class search ranking once linked.

  // Search existing labels by free text (for the "Linked label" picker).
  const searchLabels = useCallback(
    async (term: string): Promise<LabelSearchResult[]> => {
      const q = term.trim();
      if (!q) return [];
      const data = await request<{ results?: LabelSearchResult[] }>({
        method: "get",
        url: "/products/sdk/catalog/labels/search",
        params: { term: q },
      });
      return Array.isArray(data?.results) ? data.results : [];
    },
    [request]
  );

  // Auto-match: resolve each item's candidate phrases + product name to real
  // system labels (scored, best first, above the server's threshold) so the UI
  // can auto-link without depending on the ingest chain. Batch call: one request
  // returns a result per input item, keyed by index.
  const autoMatchLabels = useCallback(
    async (items: LabelAutoMatchInput[]): Promise<LabelAutoMatchResult[]> => {
      if (!items.length) return [];
      const data = await request<{ results?: LabelAutoMatchResult[] }>({
        method: "post",
        url: "/products/sdk/catalog/labels/automatch",
        data: { items },
      });
      return Array.isArray(data?.results) ? data.results : [];
    },
    [request]
  );

  // Load the label link(s) currently on a food (for display + edit).
  const getFoodLabels = useCallback(
    async (foodId: string, source = "passio"): Promise<FoodLabelLink[]> => {
      if (!foodId) return [];
      const data = await request<{ labels?: FoodLabelLink[] }>({
        method: "get",
        url: `/products/sdk/catalog/food-labels/${encodeURIComponent(foodId)}`,
        params: source && source !== "passio" ? { source } : undefined,
      });
      return Array.isArray(data?.labels) ? data.labels : [];
    },
    [request]
  );

  // Link a food to an existing label (creates a NON-default reference).
  const linkFoodLabel = useCallback(
    (params: {
      labelId: string;
      foodId: string;
      source?: string;
    }): Promise<unknown> =>
      request({
        method: "post",
        url: "/products/sdk/catalog/food/labels",
        data: {
          labelId: params.labelId,
          foodId: params.foodId,
          source: params.source || "passio",
        },
      }),
    [request]
  );

  // ----- product / brand images (convention-based CDN manager, PRO-gated) -----
  // Uploads copy an image (from a File or a remote URL) into the public CDN
  // bucket under product-images/{foodId} or brand-images/{brandSlug}. Linking is
  // by convention (no data writes); a batch lookup reports which items have one.

  const uploadProductImage = useCallback(
    (id: string, input: File | string): Promise<CatalogImageResult> => {
      if (!id) throw new Error("uploadProductImage requires a food id");
      const url = `/products/sdk/catalog/images/product/${encodeURIComponent(
        id
      )}`;
      if (typeof input === "string") {
        return request<CatalogImageResult>({
          method: "post",
          url,
          data: { sourceUrl: input },
        });
      }
      const form = new FormData();
      form.append("file", input);
      return request<CatalogImageResult>({ method: "post", url, data: form });
    },
    [request]
  );

  const deleteProductImage = useCallback(
    (id: string): Promise<{ deleted: boolean }> => {
      if (!id) throw new Error("deleteProductImage requires a food id");
      return request<{ deleted: boolean }>({
        method: "delete",
        url: `/products/sdk/catalog/images/product/${encodeURIComponent(id)}`,
      });
    },
    [request]
  );

  // `foodId`, when provided, is the product the user was editing when they
  // attached this brand logo; the server stamps a catalog:edit origin on it so
  // that product surfaces in the customer's contributions list (attaching an
  // image is enough to "claim" a record — no full save required).
  const uploadBrandImage = useCallback(
    (
      brandName: string,
      input: File | string,
      foodId?: string
    ): Promise<CatalogImageResult> => {
      const name = (brandName || "").trim();
      if (!name) throw new Error("uploadBrandImage requires a brand name");
      const url = "/products/sdk/catalog/images/brand";
      const params = foodId ? { foodId } : undefined;
      if (typeof input === "string") {
        return request<CatalogImageResult>({
          method: "post",
          url,
          params,
          data: { sourceUrl: input, brandName: name },
        });
      }
      const form = new FormData();
      form.append("file", input);
      form.append("brandName", name);
      return request<CatalogImageResult>({
        method: "post",
        url,
        params,
        data: form,
      });
    },
    [request]
  );

  const deleteBrandImage = useCallback(
    (brandName: string): Promise<{ deleted: boolean }> => {
      const name = (brandName || "").trim();
      if (!name) throw new Error("deleteBrandImage requires a brand name");
      return request<{ deleted: boolean }>({
        method: "delete",
        url: "/products/sdk/catalog/images/brand",
        params: { brandName: name },
      });
    },
    [request]
  );

  // Batch-discover which products/brands have an image. Returns maps keyed by
  // the exact id / brand name supplied; only entries that HAVE an image appear.
  const lookupImages = useCallback(
    async (args: {
      productIds?: string[];
      brandNames?: string[];
    }): Promise<CatalogImageLookup> => {
      const productIds = (args.productIds || []).filter(Boolean);
      const brandNames = (args.brandNames || []).filter(Boolean);
      if (productIds.length === 0 && brandNames.length === 0) {
        return { products: {}, brands: {} };
      }
      const data = await request<{
        products?: Record<string, string>;
        brands?: Record<string, string>;
      }>({
        method: "post",
        url: "/products/sdk/catalog/images/lookup",
        data: { productIds, brandNames },
      });
      return {
        products: data?.products || {},
        brands: data?.brands || {},
      };
    },
    [request]
  );

  // ----- chain execution (proxied through products /sdk) -----

  // Kick off a chain and return the started execution record IMMEDIATELY (with
  // its id), without waiting for completion. Used by the background job manager
  // so it can persist the execution id and poll independently (surviving a
  // hidden overlay / tab reload). `runChain` remains the fire-and-await helper.
  const startChain = useCallback(
    async (
      chainId: string,
      inputData: Record<string, unknown>,
      opts: { metadata?: Record<string, unknown>; signal?: AbortSignal } = {}
    ): Promise<ChainExecutionResponse> => {
      if (!chainId) throw new Error("No chain id configured.");
      return request<ChainExecutionResponse>({
        method: "post",
        url: `/products/sdk/chains/${encodeURIComponent(chainId)}/execute`,
        data: {
          input_data: inputData,
          metadata: { source: "catalog-manager", ...(opts.metadata || {}) },
        },
        signal: opts.signal,
      });
    },
    [request]
  );

  // Fetch the current state of a chain execution (for background polling).
  const getChainExecution = useCallback(
    (executionId: string): Promise<ChainExecutionResponse> =>
      request<ChainExecutionResponse>({
        method: "get",
        url: `/products/sdk/chains/executions/${encodeURIComponent(
          executionId
        )}`,
      }),
    [request]
  );

  const runChain = useCallback(
    async (
      chainId: string,
      inputData: Record<string, unknown>,
      opts: RunChainOptions = {}
    ): Promise<ChainExecutionResponse> => {
      if (!chainId) {
        throw new Error(
          "No chain id configured. Set it in Catalog Manager settings (gear icon) or via env."
        );
      }
      const started = await request<ChainExecutionResponse>({
        method: "post",
        url: `/products/sdk/chains/${encodeURIComponent(chainId)}/execute`,
        data: {
          input_data: inputData,
          metadata: { source: "catalog-manager", ...(opts.metadata || {}) },
          node_overrides: opts.nodeOverrides,
        },
        signal: opts.signal,
      });

      const execId = started?.id;
      if (!execId) throw new Error("Chain did not return an execution id");

      const interval = opts.pollIntervalMs ?? 2500;
      const timeout = opts.timeoutMs ?? 12 * 60 * 1000;
      const deadline = Date.now() + timeout;

      let last: ChainExecutionResponse = started;
      opts.onStatus?.(started.status, started.total_ai_credits_used);
      opts.onProgress?.(extractChainProgress(started));

      while (Date.now() < deadline) {
        if (opts.signal?.aborted)
          throw new DOMException("Aborted", "AbortError");
        if (last.status === "completed" || last.status === "failed") break;
        await new Promise((r) => setTimeout(r, interval));
        last = await request<ChainExecutionResponse>({
          method: "get",
          url: `/products/sdk/chains/executions/${encodeURIComponent(execId)}`,
          signal: opts.signal,
        });
        opts.onStatus?.(last.status, last.total_ai_credits_used);
        opts.onProgress?.(extractChainProgress(last));
      }

      if (last.status === "failed") {
        throw new Error(last.error || "Chain execution failed");
      }
      if (last.status !== "completed") {
        throw new Error("Chain execution timed out");
      }
      return last;
    },
    [request]
  );

  return useMemo(
    () => ({
      searchFoods,
      getFoodByRefCode,
      getFoodById,
      getFoodByUpc,
      createFood,
      updateFood,
      recheckConcerns,
      saveReplacementReview,
      getMyContributions,
      notifyChange,
      searchLabels,
      autoMatchLabels,
      getFoodLabels,
      linkFoodLabel,
      uploadProductImage,
      deleteProductImage,
      uploadBrandImage,
      deleteBrandImage,
      lookupImages,
      startChain,
      getChainExecution,
      runChain,
    }),
    [
      searchFoods,
      getFoodByRefCode,
      getFoodById,
      getFoodByUpc,
      createFood,
      updateFood,
      recheckConcerns,
      saveReplacementReview,
      getMyContributions,
      notifyChange,
      searchLabels,
      autoMatchLabels,
      getFoodLabels,
      linkFoodLabel,
      uploadProductImage,
      deleteProductImage,
      uploadBrandImage,
      deleteBrandImage,
      lookupImages,
      startChain,
      getChainExecution,
      runChain,
    ]
  );
};

export type CatalogApi = ReturnType<typeof useCatalogApi>;
